import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { listSubscriptions } from '../../services';
import DataTable from '../../components/DataTable';
import UserSearchSelect from '../../components/UserSearchSelect';
import CreateSubscriptionDialog from './CreateSubscriptionDialog';
import EditSubscriptionDialog from './EditSubscriptionDialog';

const PAGE_SIZE = 20;
const SERVICE_OPTIONS = [
  { value: '', label: 'Tất cả dịch vụ' },
  { value: 'fb_graph', label: 'FB Graph' },
  { value: 'fb_action', label: 'FB Action' },
  { value: 'tiktok', label: 'TikTok' },
];
const STATUS_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  { value: 0, label: 'Active' },
  { value: 1, label: 'Expired' },
  { value: 2, label: 'Reached Limit' },
];
const STATUS_LABELS = { 0: 'Active', 1: 'Expired', 2: 'Reached Limit' };
const FREEMIUM_OPTIONS = [
  { value: '', label: 'Tất cả (freemium + paid)' },
  { value: 'true', label: 'Freemium' },
  { value: 'false', label: 'Paid' },
];

function getErrorMessage(error) {
  return error.response?.data?.error || error.response?.data?.message || error.message || 'Request failed';
}

// Khung xám bo góc = phần đã dùng (used), phần tô màu = phần còn lại (remain) -
// nhận usedPercent (0-100, "đã dùng bao nhiêu %") và tự đảo thành width phần
// remain, để khớp đúng ý nghĩa text "Remains N..." đè lên trên.
function PercentBar({ usedPercent, text, colorClassName }) {
  const remainPercent = 100 - Math.min(100, Math.max(0, usedPercent));
  return (
    <div className="relative h-6 w-full min-w-[140px] overflow-hidden rounded border border-slate-300 bg-slate-200">
      <div
        className={`absolute left-0 top-0 h-full ${colorClassName}`}
        style={{ width: `${remainPercent}%`, transition: 'width 0.3s' }}
      />
      <div className="absolute inset-0 flex items-center justify-center text-xs text-black">{text}</div>
    </div>
  );
}

export default function SubscriptionsTab() {
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [serviceCallback, setServiceCallback] = useState('');
  const [status, setStatus] = useState('');
  const [isFreemium, setIsFreemium] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    listSubscriptions({
      search,
      userIds: selectedUsers.map((u) => u.id),
      serviceCallback,
      status: status === '' ? undefined : status,
      isFreemium: isFreemium === '' ? undefined : isFreemium === 'true',
      sortBy,
      sortDir,
      page,
      pageSize: PAGE_SIZE,
    })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((error) => {
        if (!cancelled) toast.error(getErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [search, selectedUsers, serviceCallback, status, isFreemium, sortBy, sortDir, page, reloadToken]);

  function getUsedRequestsPercent(row) {
    if (!row.requests_limit) return 0;
    return (row.requests_used / row.requests_limit) * 100;
  }

  // Tính theo chu kỳ gia hạn gần nhất (expired_at - duration_days -> expired_at),
  // KHÔNG phải từ started_at - vì started_at là mốc tạo subscription lần đầu,
  // không đổi qua các lần renew, trong khi duration_days phản ánh đúng độ dài
  // 1 chu kỳ hiện tại (xem SubscriptionRow.js's daysPercent ở ak_api_frontend's
  // AdminPanel cho cùng ý tưởng, nhưng khác ở chỗ dùng đúng duration_days thật
  // của từng subscription thay vì hardcode 31 ngày).
  function getUsedDurationPercent(row) {
    const expiredAt = new Date(row.expired_at).getTime();
    const durationMs = (row.duration_days || 0) * 24 * 60 * 60 * 1000;
    const cycleStartedAt = expiredAt - durationMs;
    const now = Date.now();
    if (!(durationMs > 0)) return 0;
    const percent = ((now - cycleStartedAt) / durationMs) * 100;
    return Math.min(100, Math.max(0, percent));
  }

  const columns = [
    { key: 'username', label: 'username', maxWidth: 200, wrap: 'wrap' },
    { key: 'id', label: 'id', sortable: true },
    { key: 'service_callback', label: 'service' },
    { key: 'note', label: 'note' },
    {
      key: 'requests_limit',
      label: 'requests_limit',
      sortable: true,
      render: (row) => row.requests_limit?.toLocaleString(),
    },
    {
      key: 'requests_used',
      label: 'requests_used',
      sortable: true,
      render: (row) => row.requests_used?.toLocaleString(),
    },
    {
      key: 'bought_price',
      label: 'bought_price',
      sortable: true,
      render: (row) => "$" + row.bought_price?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
    },
    {
      key: 'used_requests_percent',
      label: 'quota',
      render: (row) => (
        <PercentBar
          usedPercent={getUsedRequestsPercent(row)}
          text={`Remains ${(row.requests_remain ?? row.requests_limit - row.requests_used)?.toLocaleString()} calls`}
          colorClassName="bg-green-300"
        />
      ),
    },
    {
      key: 'used_duration_percent',
      label: 'duration',
      render: (row) => {
        const daysRemain = (new Date(row.expired_at).getTime() - Date.now()) / (24 * 60 * 60 * 1000);
        return (
          <PercentBar
            usedPercent={getUsedDurationPercent(row)}
            text={`Remains ${daysRemain.toFixed(2)} days`}
            colorClassName="bg-blue-300"
          />
        );
      },
    },
    {
      key: 'status',
      label: 'status',
      render: (row) => STATUS_LABELS[row.status] ?? row.status,
    },
    {
      key: 'expired_at',
      label: 'expired_at',
      sortable: true,
      render: (row) => new Date(row.expired_at).toLocaleString(),
    },
    {
      key: 'actions',
      label: 'actions',
      render: (row) => (
        <button
          type="button"
          onClick={() => setEditingSubscription(row)}
          className="rounded-lg border border-cyan-200 px-3 py-1.5 text-xs text-cyan-700"
        >
          Sửa
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-cyan-100 bg-white p-3">
        <div className="grid gap-3 md:grid-cols-5">
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Tìm theo ghi chú/tên"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-cyan-400"
          />
          <select
            value={serviceCallback}
            onChange={(event) => {
              setServiceCallback(event.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-cyan-400"
          >
            {SERVICE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value === '' ? '' : Number(event.target.value));
              setPage(1);
            }}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-cyan-400"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={isFreemium}
            onChange={(event) => {
              setIsFreemium(event.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-cyan-400"
          >
            {FREEMIUM_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <UserSearchSelect
            value={selectedUsers}
            onChange={(users) => {
              setSelectedUsers(users);
              setPage(1);
            }}
            multiple
            placeholder="Lọc theo user..."
          />
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white"
          >
            Tạo subscription
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={(nextSortBy, nextSortDir) => {
          setSortBy(nextSortBy);
          setSortDir(nextSortDir);
        }}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        stickyColumnKey="username"
      />

      {isCreateOpen && (
        <CreateSubscriptionDialog
          onClose={() => setIsCreateOpen(false)}
          onCreated={() => {
            setIsCreateOpen(false);
            setReloadToken((t) => t + 1);
          }}
        />
      )}

      {editingSubscription && (
        <EditSubscriptionDialog
          subscription={editingSubscription}
          onClose={() => setEditingSubscription(null)}
          onUpdated={() => {
            setEditingSubscription(null);
            setReloadToken((t) => t + 1);
          }}
          onDeleted={() => {
            setEditingSubscription(null);
            setReloadToken((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}
